import { useLocation } from "react-router-dom";
import { useHubBasePath } from "../../../utils/hubPaths";
import { useAdminAppMode } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import ChatInboxHub from "../common/ChatInboxHub";
import ChatConversationPage from "../common/ChatConversationPage";
import SupportConversationPage from "../../common/SupportChat/SupportConversationPage";
import { initials } from "../common/ChatThread";
import { personDisplayName } from "../../../utils/clientAppUtils";
import md from "../common/manageDesktop.module.css";

function ClubChatPage({ hubBase, desktop = false }) {
  return (
    <ChatConversationPage
      title="Чат клуба"
      subtitle="Общий чат с участниками клуба"
      backTo={desktop ? undefined : `${hubBase}/chat`}
      threadType="club"
      mode="staff"
      avatarLabel="🏟"
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function GroupChatPage({ hubBase, groupId, groupName, desktop = false }) {
  return (
    <ChatConversationPage
      title={groupName || "Группа"}
      subtitle="Чат тренировочной группы"
      backTo={desktop ? undefined : `${hubBase}/chat`}
      threadType="group"
      groupId={groupId}
      mode="staff"
      avatarLabel="👥"
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function ClientChatPage({ patientId, clientName, hubBase, desktop = false }) {
  const name = clientName || "Клиент";
  return (
    <ChatConversationPage
      title={name}
      subtitle="Личные сообщения"
      backTo={desktop ? undefined : `${hubBase}/chat`}
      threadType="direct"
      mode="staff"
      patientId={patientId}
      avatarLabel={initials(name)}
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function MemberChatPage({ peerUserId, peerName, hubBase, desktop = false }) {
  const name = personDisplayName(peerName) || peerName || "Клиент";
  return (
    <ChatConversationPage
      title={name}
      subtitle="Личные сообщения"
      backTo={desktop ? undefined : `${hubBase}/chat`}
      threadType="member"
      mode="staff"
      peerUserId={peerUserId}
      avatarLabel={initials(name)}
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function ChatDesktopPlaceholder() {
  return (
    <div className={md.desktopChatPlaceholder}>
      <p className={md.desktopChatPlaceholderTitle}>Выберите чат</p>
      <p className={md.desktopChatPlaceholderHint}>
        Слева — переписки с клиентами, личные сообщения и чаты групп
      </p>
    </div>
  );
}

function SupportChatPage({ hubBase, desktop = false }) {
  return (
    <SupportConversationPage
      mode="club"
      shell="admin"
      title="Поддержка Бай.Спорт"
      subtitle="Команда платформы"
      backTo={desktop ? undefined : `${hubBase}/chat`}
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function resolveDesktopConversation(path, hubBase, state) {
  if (path === `${hubBase}/chat/support`) {
    return <SupportChatPage hubBase={hubBase} desktop />;
  }

  if (path === `${hubBase}/chat/club`) {
    return <ClubChatPage hubBase={hubBase} desktop />;
  }

  const groupMatch = path.match(/\/chat\/group\/([^/]+)$/);
  if (groupMatch) {
    return (
      <GroupChatPage
        hubBase={hubBase}
        groupId={groupMatch[1]}
        groupName={state?.name}
        desktop
      />
    );
  }

  const memberMatch = path.match(/\/chat\/member\/([^/]+)$/);
  if (memberMatch) {
    return (
      <MemberChatPage
        hubBase={hubBase}
        peerUserId={memberMatch[1]}
        peerName={state?.name}
        desktop
      />
    );
  }

  const clientMatch = path.match(/\/chat\/client\/([^/]+)$/);
  if (clientMatch) {
    return (
      <ClientChatPage
        hubBase={hubBase}
        patientId={clientMatch[1]}
        clientName={state?.clientName}
        desktop
      />
    );
  }

  return null;
}

function AdminChatDesktopLayout({ hubBase, pathname, state }) {
  const conversation = resolveDesktopConversation(pathname, hubBase, state);

  return (
    <div className={md.desktopChatSplit}>
      <aside className={md.desktopChatSidebar} aria-label="Список чатов">
        <ChatInboxHub portal="staff" embedded />
      </aside>
      <section className={md.desktopChatMain} aria-label="Переписка">
        {conversation || <ChatDesktopPlaceholder />}
      </section>
    </div>
  );
}

export default function AdminChatRoutes() {
  const { pathname, state } = useLocation();
  const hubBase = useHubBasePath();
  const path = pathname.replace(/\/$/, "");
  const isAdminApp = useAdminAppMode();
  const isMobile = useIsMobile(768);
  const isDesktop = isAdminApp && !isMobile;

  if (isDesktop) {
    return (
      <AdminChatDesktopLayout
        hubBase={hubBase}
        pathname={path}
        state={state}
      />
    );
  }

  if (path === `${hubBase}/chat/club`) {
    return <ClubChatPage hubBase={hubBase} />;
  }

  if (path === `${hubBase}/chat/support`) {
    return <SupportChatPage hubBase={hubBase} />;
  }

  const groupMatch = path.match(/\/chat\/group\/([^/]+)$/);
  if (groupMatch) {
    return (
      <GroupChatPage
        hubBase={hubBase}
        groupId={groupMatch[1]}
        groupName={state?.name}
      />
    );
  }

  const memberMatch = path.match(/\/chat\/member\/([^/]+)$/);
  if (memberMatch) {
    return (
      <MemberChatPage
        hubBase={hubBase}
        peerUserId={memberMatch[1]}
        peerName={state?.name}
      />
    );
  }

  const clientMatch = path.match(/\/chat\/client\/([^/]+)$/);
  if (clientMatch) {
    return (
      <ClientChatPage
        hubBase={hubBase}
        patientId={clientMatch[1]}
        clientName={state?.clientName}
      />
    );
  }

  return <ChatInboxHub portal="staff" />;
}
